import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@mono/ui-components';
import { Download } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useAttachmentPreview } from '../../hooks/useAttachmentPreview';
import { artifactClient } from '../../lib/connect/clients';
import { formatFileSize } from '../../utils/formatFileSize';
import { ErrorBoundary } from '../ErrorBoundary';
import { ExcelIcon, PdfIcon, PowerPointIcon, WordIcon } from '../icons/file-icons';
import { GenericFileIcon, ImageIcon } from '../icons/file-icons-legacy';
export const AttachmentCard = /*#__PURE__*/ memo(function AttachmentCard({ attachment }) {
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    // Use React Query for attachment preview
    const { data: previewUrl, isLoading, error } = useAttachmentPreview({
        attachmentId: attachment.id,
        enabled: isPreviewOpen && !!attachment.id
    });
    // Memoize file extension extraction
    const fileExtension = useMemo(()=>attachment.name.split('.').pop()?.toLowerCase(), [
        attachment.name
    ]);
    // Memoize icon component to prevent re-renders
    const fileIcon = useMemo(()=>{
        const iconProps = {
            className: 'w-full h-full p-1.5'
        };
        if (attachment.contentType.startsWith('image/')) {
            return /*#__PURE__*/ _jsx(ImageIcon, {
                ...iconProps
            });
        }
        switch(fileExtension){
            case 'pdf':
                return /*#__PURE__*/ _jsx(PdfIcon, {
                    ...iconProps
                });
            case 'doc':
            case 'docx':
                return /*#__PURE__*/ _jsx(WordIcon, {
                    ...iconProps
                });
            case 'xls':
            case 'xlsx':
            case 'csv':
                return /*#__PURE__*/ _jsx(ExcelIcon, {
                    ...iconProps
                });
            case 'ppt':
            case 'pptx':
                return /*#__PURE__*/ _jsx(PowerPointIcon, {
                    ...iconProps
                });
            default:
                return /*#__PURE__*/ _jsx(GenericFileIcon, {
                    ...iconProps
                });
        }
    }, [
        fileExtension,
        attachment.contentType
    ]);
    // Memoize file type description
    const fileTypeDescription = useMemo(()=>{
        if (attachment.contentType.startsWith('image/')) return 'Image';
        if (attachment.contentType.startsWith('video/')) return 'Video';
        if (attachment.contentType.startsWith('audio/')) return 'Audio';
        switch(fileExtension){
            case 'pdf':
                return 'PDF document';
            case 'doc':
            case 'docx':
                return 'Word document';
            case 'xls':
            case 'xlsx':
                return 'Excel spreadsheet';
            case 'csv':
                return 'CSV file';
            case 'ppt':
            case 'pptx':
                return 'PowerPoint presentation';
            case 'txt':
                return 'Text file';
            default:
                return 'File';
        }
    }, [
        fileExtension,
        attachment.contentType
    ]);
    const formattedFileSize = formatFileSize(attachment.size);
    // Memoize canPreview check
    const canPreview = useMemo(()=>attachment.contentType.startsWith('image/') || attachment.contentType === 'application/pdf', [
        attachment.contentType
    ]);
    const handleClick = useCallback(()=>{
        if (!attachment.id) return;
        setIsPreviewOpen(true);
    }, [
        attachment.id
    ]);
    const handleDownload = useCallback(async ()=>{
        if (!attachment.id) return;
        // Open the popup synchronously inside the click gesture, then redirect
        // it once the presigned URL resolves. Awaiting first lets the browser
        // strip the user-gesture context and most popup blockers refuse the
        // window. URL includes a Content-Disposition override so the browser
        // saves with the artifact's user-facing filename.
        const win = window.open('', '_blank');
        try {
            const dl = await artifactClient.generateDownloadUrl({
                artifactId: attachment.id
            });
            if (dl.url && win) {
                win.location.href = dl.url;
            } else {
                win?.close();
            }
        } catch (err) {
            console.error('Failed to generate download URL', err);
            win?.close();
        }
    }, [
        attachment.id
    ]);
    const handleClosePreview = useCallback(()=>{
        setIsPreviewOpen(false);
    }, []);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                className: "bg-muted/30 border rounded-lg p-2.5 flex items-center gap-3 cursor-pointer hover:bg-muted/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary w-full text-left",
                onClick: handleClick,
                "aria-label": `View attachment: ${attachment.name}`,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "bg-background dark:bg-white rounded-md w-9 h-9 flex items-center justify-center flex-shrink-0 shadow-sm overflow-hidden",
                        children: fileIcon
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-sm overflow-hidden flex-1",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "font-medium truncate text-foreground",
                                title: attachment.name,
                                children: attachment.name
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    fileTypeDescription,
                                    attachment.size && ` · ${formattedFileSize}`
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Dialog, {
                open: isPreviewOpen,
                onOpenChange: (open)=>!open && handleClosePreview(),
                children: /*#__PURE__*/ _jsxs(DialogContent, {
                    className: "max-w-4xl max-h-[90vh] overflow-hidden flex flex-col",
                    children: [
                        /*#__PURE__*/ _jsx(DialogHeader, {
                            children: /*#__PURE__*/ _jsxs(DialogTitle, {
                                className: "flex items-center justify-between",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "truncate pr-4",
                                        children: attachment.name
                                    }),
                                    /*#__PURE__*/ _jsxs("button", {
                                        onClick: handleDownload,
                                        "aria-label": `Download ${attachment.name}`,
                                        className: "flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90",
                                        children: [
                                            /*#__PURE__*/ _jsx(Download, {
                                                className: "w-4 h-4"
                                            }),
                                            "Download"
                                        ]
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(ErrorBoundary, {
                            fallback: /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col items-center justify-center h-64 gap-4",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-destructive",
                                        children: "Failed to render preview"
                                    }),
                                    /*#__PURE__*/ _jsxs("button", {
                                        onClick: handleDownload,
                                        className: "flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90",
                                        children: [
                                            /*#__PURE__*/ _jsx(Download, {
                                                className: "w-4 h-4"
                                            }),
                                            "Download instead"
                                        ]
                                    })
                                ]
                            }),
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "flex-1 overflow-auto",
                                children: isLoading ? /*#__PURE__*/ _jsx("div", {
                                    className: "flex items-center justify-center h-64",
                                    children: /*#__PURE__*/ _jsx("p", {
                                        className: "text-muted-foreground",
                                        children: "Loading..."
                                    })
                                }) : error ? /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col items-center justify-center h-64 gap-4",
                                    children: [
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "text-destructive",
                                            children: [
                                                "Error: ",
                                                error instanceof Error ? error.message : 'Failed to load attachment'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("button", {
                                            onClick: handleDownload,
                                            className: "flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90",
                                            children: [
                                                /*#__PURE__*/ _jsx(Download, {
                                                    className: "w-4 h-4"
                                                }),
                                                "Download instead"
                                            ]
                                        })
                                    ]
                                }) : previewUrl && canPreview ? /*#__PURE__*/ _jsx("div", {
                                    className: "w-full h-full",
                                    children: attachment.contentType.startsWith('image/') ? /*#__PURE__*/ _jsx("img", {
                                        src: previewUrl,
                                        alt: attachment.name,
                                        className: "w-full h-auto object-contain"
                                    }) : attachment.contentType === 'application/pdf' ? /*#__PURE__*/ _jsx("iframe", {
                                        src: previewUrl,
                                        className: "w-full h-[70vh]",
                                        title: attachment.name
                                    }) : null
                                }) : /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col items-center justify-center h-64 gap-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-muted-foreground",
                                            children: "Preview not available for this file type"
                                        }),
                                        /*#__PURE__*/ _jsxs("button", {
                                            onClick: handleDownload,
                                            className: "flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90",
                                            children: [
                                                /*#__PURE__*/ _jsx(Download, {
                                                    className: "w-4 h-4"
                                                }),
                                                "Download to view"
                                            ]
                                        })
                                    ]
                                })
                            })
                        })
                    ]
                })
            })
        ]
    });
});
